import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import {
  KeyRound,
  ArrowRight,
  Shield,
  Clock,
  AlertTriangle,
  CheckCircle,
  FileKey,
  Link2,
  Ban,
  HelpCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { HeirShell } from '@/components/HeirShell';

const steps = [
  {
    id: 'identity',
    title: 'Identity = Keypair',
    icon: KeyRound,
    content: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          On Nostr, your identity is your private key. There is no username recovery, no password reset, 
          and no central authority to appeal to. If you lose your nsec, that identity is gone forever.
        </p>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <h4 className="text-sm font-medium text-cyan-400 mb-2 flex items-center space-x-2">
            <KeyRound className="w-4 h-4" />
            <span>What this means</span>
          </h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start space-x-2">
              <Ban className="w-4 h-4 text-red-400 mt-0.5 shrink-0" />
              <span>There is no "key rotation" — a new nsec is a new identity</span>
            </li>
            <li className="flex items-start space-x-2">
              <ArrowRight className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
              <span>NIP-41 only migrates <em>followers</em>, not your data</span>
            </li>
            <li className="flex items-start space-x-2">
              <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
              <span>Your old posts, reactions, and zaps stay with the old key forever</span>
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    id: 'chain',
    title: 'Forward-Only Chain',
    icon: Link2,
    content: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          NIP-41 creates a one-way chain of succession. Key A whitelists key B as its successor. 
          Key B can then migrate from A, and later whitelist key C. The chain only moves forward.
        </p>
        <div className="flex items-center justify-center py-4">
          <div className="flex items-center space-x-4">
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                <span className="text-sm font-mono text-slate-400">A</span>
              </div>
              <span className="text-xs text-slate-500">Old</span>
            </div>
            <ArrowRight className="w-5 h-5 text-cyan-400" />
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-cyan-950/30 border border-cyan-500/40 flex items-center justify-center">
                <span className="text-sm font-mono text-cyan-400">B</span>
              </div>
              <span className="text-xs text-cyan-400">Successor</span>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-600" />
            <div className="flex flex-col items-center space-y-2">
              <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                <span className="text-sm font-mono text-slate-400">C</span>
              </div>
              <span className="text-xs text-slate-500">Next</span>
            </div>
          </div>
        </div>
        <div className="bg-amber-950/20 border border-amber-500/20 rounded-lg p-4">
          <p className="text-sm text-amber-300">
            <strong>One whitelist = one migration.</strong> After B migrates from A, A's whitelist is consumed. 
            B must then whitelist C if the chain is to continue.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'events',
    title: 'Three Events',
    icon: FileKey,
    content: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          NIP-41 uses three distinct events. Two are signed by you, one is a Bitcoin timestamp attestation.
        </p>
        <div className="grid gap-3">
          <EventTypeCard
            kind={1776}
            name="Whitelist"
            signer="Active key (A)"
            description="Pre-positions a successor. Signed while your key is still trusted."
            color="cyan"
          />
          <EventTypeCard
            kind={1040}
            name="Timestamp Proof"
            signer="OpenTimestamps"
            description="Anchors the whitelist to Bitcoin time. Required for adjudicating competing migrations."
            color="amber"
          />
          <EventTypeCard
            kind={1777}
            name="Migration"
            signer="Successor key (B)"
            description="The break-glass declaration. Only the successor can publish this."
            color="green"
          />
        </div>
      </div>
    ),
  },
  {
    id: 'limitations',
    title: 'What Does NOT Migrate',
    icon: Ban,
    content: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          This is the most important limitation. NIP-41 only updates <em>contact lists</em> on supported clients. 
          Everything else stays behind.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <NonMigratedItem icon={Ban} label="Old posts & threads" />
          <NonMigratedItem icon={Ban} label="Reactions & zaps" />
          <NonMigratedItem icon={Ban} label="DM history" />
          <NonMigratedItem icon={Ban} label="Badges & awards" />
          <NonMigratedItem icon={Ban} label="Relay lists" />
          <NonMigratedItem icon={Ban} label="App-specific data" />
        </div>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <p className="text-sm text-slate-400">
            The new key starts with a blank slate. Your followers on NIP-41-aware clients will 
            automatically follow the new key, but your history does not transfer.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'window',
    title: 'Contestation Window',
    icon: Clock,
    content: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          Publishing a migration does not immediately move your followers. There is a{' '}
          <strong className="text-amber-400">60-day contestation window</strong> first.
        </p>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-medium text-slate-200">Why the wait?</h4>
          <p className="text-sm text-slate-400">
            If an attacker compromises your key and publishes a fake migration, the window gives 
            <em> you</em> time to notice and publish a competing migration pointing to your 
            real successor.
          </p>
          <div className="flex items-center space-x-3 py-2">
            <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500 rounded-full w-1/3" />
            </div>
            <span className="text-xs font-mono text-amber-400">60 days</span>
          </div>
          <p className="text-sm text-slate-400">
            The migration backed by the <em>older</em> whitelist timestamp wins. This is why 
            arming early matters — a pre-compromise whitelist beats a post-compromise one.
          </p>
        </div>
      </div>
    ),
  },
  {
    id: 'prep',
    title: 'Pre-position or Lose the Option',
    icon: Shield,
    content: (
      <div className="space-y-4">
        <p className="text-slate-300 leading-relaxed">
          You cannot whitelist a successor <em>after</em> a key is compromised. Arming only works 
          while your key is still trusted. This is the single most important behavioral takeaway.
        </p>
        <div className="bg-red-950/20 border border-red-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-red-400 mb-1">The Risk</h4>
              <p className="text-sm text-red-300/80">
                If your key leaks and you have not armed a successor, there is no NIP-41 recovery path. 
                Your only option is to manually tell followers about a new key out of band.
              </p>
            </div>
          </div>
        </div>
        <div className="bg-cyan-950/20 border border-cyan-500/30 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-cyan-400 mb-1">The Solution</h4>
              <p className="text-sm text-cyan-300/80">
                Arm a successor now, while your key is healthy. Store the successor nsec in cold storage. 
                Check back periodically to confirm your whitelist is still live.
              </p>
            </div>
          </div>
        </div>
      </div>
    ),
  },
];

export default function LearnPage() {
  useSeoMeta({
    title: 'Learn — Heir (NIP-41)',
    description: 'Learn how NIP-41 key migration works and why it matters.',
  });

  const [currentStep, setCurrentStep] = useState(0);
  const step = steps[currentStep];

  return (
    <HeirShell>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-white mb-3">How NIP-41 Works</h1>
          <p className="text-slate-400">
            A step-by-step guide to key migration on Nostr
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setCurrentStep(i)}
                className={cn(
                  'flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all',
                  i === currentStep
                    ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400'
                    : i < currentStep
                    ? 'border-cyan-500/50 bg-cyan-950/30 text-cyan-600'
                    : 'border-slate-700 bg-slate-900 text-slate-600'
                )}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>

        {/* Step Card */}
        <div className="bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-slate-800">
            <div className="flex items-center space-x-3">
              <div className="p-2 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
                <step.icon className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <span className="text-xs text-slate-500 uppercase tracking-wider">
                  Step {currentStep + 1} of {steps.length}
                </span>
                <h2 className="text-xl font-semibold text-white">{step.title}</h2>
              </div>
            </div>
          </div>
          <div className="p-6">{step.content}</div>
          <div className="p-6 border-t border-slate-800 flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentStep(Math.max(0, currentStep - 1))}
              disabled={currentStep === 0}
              className="border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
            >
              Previous
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="bg-cyan-600 hover:bg-cyan-500 text-white"
              >
                Next Step
              </Button>
            ) : (
              <Button
                onClick={() => setCurrentStep(0)}
                variant="outline"
                className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-950/30"
              >
                Start Over
              </Button>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <LinkCard
            to="/inspect"
            icon={HelpCircle}
            title="Inspect"
            description="Check any npub's migration state"
          />
          <ComingSoonCard
            icon={Shield}
            title="Arm"
            description="Pre-position a successor key"
          />
          <ComingSoonCard
            icon={KeyRound}
            title="Migrate"
            description="Execute break-glass migration"
          />
        </div>
      </div>
    </HeirShell>
  );
}

function EventTypeCard({
  kind,
  name,
  signer,
  description,
  color,
}: {
  kind: number;
  name: string;
  signer: string;
  description: string;
  color: 'cyan' | 'amber' | 'green';
}) {
  const colorMap = {
    cyan: 'border-cyan-500/30 bg-cyan-950/20',
    amber: 'border-amber-500/30 bg-amber-950/20',
    green: 'border-green-500/30 bg-green-950/20',
  };

  return (
    <div className={cn('rounded-lg border p-4', colorMap[color])}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-slate-200">{name}</span>
        <span className="text-xs font-mono text-slate-500">kind:{kind}</span>
      </div>
      <p className="text-xs text-slate-400 mb-2">{description}</p>
      <div className="flex items-center space-x-2">
        <KeyRound className="w-3 h-3 text-slate-500" />
        <span className="text-xs text-slate-500">Signed by: {signer}</span>
      </div>
    </div>
  );
}

function NonMigratedItem({ icon: Icon, label }: { icon: any; label: string }) {
  return (
    <div className="flex items-center space-x-2 bg-slate-900 rounded-lg p-3 border border-slate-800">
      <Icon className="w-4 h-4 text-red-400/70 shrink-0" />
      <span className="text-sm text-slate-400">{label}</span>
    </div>
  );
}

function LinkCard({
  to,
  icon: Icon,
  title,
  description,
}: {
  to: string;
  icon: any;
  title: string;
  description: string;
}) {
  return (
    <a
      href={to}
      className="flex items-start space-x-3 p-4 rounded-xl border border-slate-800 bg-slate-950 hover:border-cyan-500/30 hover:bg-cyan-950/10 transition-all group"
    >
      <Icon className="w-5 h-5 text-slate-500 group-hover:text-cyan-400 mt-0.5 transition-colors" />
      <div>
        <h4 className="text-sm font-medium text-slate-300 group-hover:text-cyan-400 transition-colors">
          {title}
        </h4>
        <p className="text-xs text-slate-500 mt-1">{description}</p>
      </div>
    </a>
  );
}

function ComingSoonCard({
  icon: Icon,
  title,
  description,
}: {
  icon: any;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start space-x-3 p-4 rounded-xl border border-slate-800 bg-slate-950 cursor-default">
      <Icon className="w-5 h-5 text-cyan-400 mt-0.5" />
      <div>
        <div className="flex items-center space-x-2">
          <h4 className="text-sm font-medium text-slate-200">
            {title}
          </h4>
          <span className="text-[10px] uppercase tracking-wider bg-slate-800 text-slate-400 px-1.5 py-0.5 rounded">
            Coming Soon
          </span>
        </div>
        <p className="text-xs text-slate-400 mt-1">{description}</p>
      </div>
    </div>
  );
}
